import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Selectors = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎯 CSS Selectors</h1>

      <p className="tutorial-intro">
        CSS selectors define the elements on a web page that you want to style.
        They are the foundation of CSS, allowing you to target elements by tag,
        class, id, attributes, and more.
      </p>

      {/* Basic Selectors */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 Basic Selectors</h2>
        <ul className="tutorial-list">
          <li>
            <strong>Type Selector</strong> – selects all elements of a type
            (e.g., <code>p</code>, <code>h1</code>).
          </li>
          <li>
            <strong>Class Selector</strong> – selects elements with a specific
            class (e.g., <code>.intro</code>).
          </li>
          <li>
            <strong>ID Selector</strong> – selects an element with a specific ID
            (e.g., <code>#header</code>).
          </li>
        </ul>

        <CodeCopyBlock
          code={`/* Type Selector */
p {
  color: blue;
}

/* Class Selector */
.intro {
  font-size: 18px;
}

/* ID Selector */
#header {
  background: lightgray;
}`}
        />

        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <p style={{ color: "blue" }}>This is a paragraph (type selector)</p>
          <p className="intro" style={{ fontSize: "18px", margin: 0 }}>
            This is a class selector
          </p>
          <div
            id="header"
            style={{
              background: "lightgray",
              padding: "5px",
              marginTop: "0.5rem",
            }}
          >
            This is an ID selector
          </div>
        </div>
      </div>

      {/* Grouping & Universal Selector */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Grouping & Universal Selector</h2>
        <CodeCopyBlock
          code={`/* Grouping */
h1, h2, p {
  margin: 10px;
}

/* Universal Selector */
* {
  font-family: Arial, sans-serif;
}`}
        />
        <p>
          - <strong>Grouping</strong> lets you apply the same styles to multiple
          selectors. - <strong>Universal Selector (*)</strong> targets all
          elements.
        </p>
      </div>

      {/* Attribute Selectors */}
      <div className="tutorial-section">
        <h2 className="section-title">🔎 Attribute Selectors</h2>
        <p>
          You can style elements based on attributes like <code>type</code>,{" "}
          <code>href</code>, or <code>src</code>.
        </p>
        <CodeCopyBlock
          code={`/* Input fields of type text */
input[type="text"] {
  border: 1px solid #333;
  padding: 5px;
}

/* Links that open in new tab */
a[target="_blank"] {
  color: red;
}`}
        />

        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <input type="text" placeholder="Text input" />
          <br />
          <a href="/" target="_blank" style={{ color: "red" }}>
            This is a styled link
          </a>
        </div>
      </div>

      {/* Pseudo-classes */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Pseudo-classes</h2>
        <p>
          Pseudo-classes define the special state of an element (like hover,
          active, first-child).
        </p>
        <CodeCopyBlock
          code={`a:hover {
  color: green;
}

p:first-child {
  font-weight: bold;
}`}
        />
      </div>

      {/* Pseudo-elements */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Pseudo-elements</h2>
        <p>
          Pseudo-elements allow you to style parts of an element, such as the
          first line or add content before/after elements.
        </p>
        <CodeCopyBlock
          code={`p::first-line {
  text-transform: uppercase;
}

p::after {
  content: " 🌟";
}`}
        />
      </div>

      {/* Highlighted Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef2f2, #fecaca)",
          padding: "1rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#991b1b", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul style={{ lineHeight: "1.8", paddingLeft: "1.5rem" }}>
          <li>
            Use <strong>type, class, and ID selectors</strong> for basic
            styling.
          </li>
          <li>
            Attribute selectors let you style based on element properties.
          </li>
          <li>Pseudo-classes handle states like hover and active.</li>
          <li>Pseudo-elements style specific parts of elements.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know how selectors work, continue with{" "}
          <a href="/css/colors" className="learn-more">
            CSS Colors
          </a>{" "}
          to start styling elements with different color schemes.
        </p>
      </div>
    </div>
  );
};

export default CSS_Selectors;
